// UserRank.jsx
/**
 * UserRank
 * --------------------------------------------------------------------
 * 목적:
 *  - 홈/리포트 상단 카드 형태로 "금일/금주/금월 단골 상위 3명" 요약을 표시.
 *  - 상세 보기는 /rank 라우트로 이동하여 RankPage에서 테이블로 확인.
 *
 * 주요 포인트:
 *  - rankDetailValue[page]를 useMemo로 메모이제이션 → 부모 리렌더 시 참조 안정성.
 *  - 상위 3명 계산은 effect 내부에서 정렬된 복사본을 사용(원본 불변).
 */

import React, { useEffect, useMemo, useState } from "react";
import Button from "../common/Button";
import Price from "../common/Price";
import { useNavigate } from "react-router-dom";

function UserRank(props) {
  const navigate = useNavigate();

  // rankList 메모이제이션: props 변경 시에만 새로운 참조 생성
  const rankList = useMemo(
    () => props.rankDetailValue[props.page] || [],
    [props.rankDetailValue, props.page]
  );
  const hasData = rankList.length > 0;

  // 상위 3명 상태
  const [rankFirst, setRankFirst] = useState({});
  const [rankSecond, setRankSecond] = useState({});
  const [rankThird, setRankThird] = useState({});
  const [firstUrl, setFirstUrl] = useState(""); // 현재 미사용(이미지 URL 계획 시 사용)

  // rankList 변경 시 상위 3명 계산
  useEffect(() => {
    if (hasData) {
      // ⚠️주의: 여기서 count 필드 기준으로 정렬
      // API가 visit_count만 제공한다면 정렬 기준을 visit_count로 바꿔야 함.
      const sorted = [...rankList].sort((a, b) => b.count - a.count);
      setRankFirst(sorted[0] || {});
      setRankSecond(sorted[1] || {});
      setRankThird(sorted[2] || {});
    } else {
      setRankFirst({});
      setRankSecond({});
      setRankThird({});
    }
  }, [hasData, rankList]);

  // 첫 번째 유저의 이미지 URL 설정(메뉴/유저 오브젝트 사용 시)
  useEffect(() => {
    if (rankFirst.name && props.menuObject?.[rankFirst.name]) {
      setFirstUrl(props.menuObject[rankFirst.name].img);
    }
  }, [rankFirst.name, props.menuObject]);

  // 상세 페이지로 이동(현재 리스트/날짜/직전 데이터/탭을 라우팅 state로 전달)
  const navigateRankDetail = () => {
    navigate("/rank", {
      state: {
        pageType: "bestMenu",
        rankDetail: rankList,
        currentDate: props.currentDate,
        lastDetail: props.lastDetailValue,
        page: props.page,
      },
    });
  };

  // 렌더 보조 함수들(이름/방문 수 포맷)
  const renderName = (rank) => (rank?.name ? `${rank.name}님` : "-");
  const renderCount = (rank, unit) =>
    rank?.visit_count != null ? (
      <Price value={rank.visit_count} unit={unit} />
    ) : null;

  // 헤더 타이틀(i18n 키에 맞춰 기간별 문구 노출)
  const title =
    props.page === "daily"
      ? props.t("userRank.today")
      : props.page === "weekly"
      ? props.t("userRank.thisWeek")
      : props.page === "monthly"
      ? props.t("userRank.thisMonth")
      : props.t("userRank.today");

  return (
    <div className="block w-full px-[5%] pt-4 pb-8 bg-white">
      {/* 제목 */}
      <div className="font-semibold text-[5.3vw]">
        {title} {props.t("userRank.bestSellingMenu")}
      </div>

      {/* 데이터 없을 때: 가이드 이미지 + 안내 텍스트 */}
      {!hasData ? (
        <div className="flex flex-col items-center justify-center h-36 mt-8 gap-2">
          <img
            src="/assets/caution_img.png"
            alt="info icon"
            className="mb-[2vw] w-[8vw]"
          />
          <span className="text-[3.5vw] text-neutral-500">
            {props.t("Nodata")}
          </span>
        </div>
      ) : (
        <>
          {/* 상위 3명 카드: 1등/2등/3등 */}
          <div className="flex w-full mt-[8vw] gap-4 items-stretch">
            {/* 1등 강조 카드 */}
            <div className="flex flex-col items-center justify-center w-[50%] h-36 space-y-2 rounded-2xl bg-blue-700 text-white">
              <span className="text-4xl">🥇</span>
              <div className="text-[6vw] font-extrabold whitespace-nowrap overflow-hidden text-ellipsis">
                {renderName(rankFirst)}
              </div>
              <div className="text-[5vw] font-bold">
                {renderCount(rankFirst, "회 방문")}
              </div>
            </div>

            {/* 2, 3등 리스트 */}
            <div className="flex flex-col w-[50%] gap-4 items-center">
              <div className="flex items-center justify-between w-full h-16 px-4 py-2 border border-neutral-300 rounded-2xl bg-white">
                <div className="text-[4vw] font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
                  <span className="text-[4.5vw]">🥈 </span>
                  <span className="mr-1">{renderName(rankSecond)}</span>
                </div>
                <div className="text-[4vw] font-semibold">
                  {renderCount(rankSecond, "회")}
                </div>
              </div>
              <div className="flex items-center justify-between w-full h-16 px-4 py-2 border border-neutral-300 rounded-2xl bg-white">
                <div className="text-[4vw] font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
                  <span className="text-[4.5vw]">🥉 </span>
                  <span className="mr-1">{renderName(rankThird)}</span>
                </div>
                <div className="text-[4vw] font-semibold">
                  {renderCount(rankThird, "회")}
                </div>
              </div>
            </div>
          </div>

          {/* 상세 보기 버튼: /rank로 네비게이션 */}
          <div
            className="button-div mt-6"
            id="report"
            onClick={navigateRankDetail}
          >
            <Button txt={props.t("bestMenu.details")} color="transparent" />
          </div>
        </>
      )}
    </div>
  );
}

export default UserRank;
